import { query, where } from "firebase/firestore";
import { useEffect, useState } from "react";
import { getPropertyRolesLists } from "to-be-removed/api/utiles/userRights.utils";
import { CollectionLoadedListener } from "../../../to-be-removed/LocalData/General/CollectionLoader";
import { LocalData } from "../../../to-be-removed/LocalData/General/LocalData";
import { V2CollectionItemData } from "../../../to-be-removed/LocalData/General/V2CollectionItemData";
import { CollectionData } from "../../../to-be-removed/LocalData/collections/CollectionData";
import { ArrayUtilities } from "../../../utils/arrayUtilities";

export const useGlobalCollectionItems = (collection: CollectionData | undefined) => {
  const [collectionItems, setCollectionItems] = useState<V2CollectionItemData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!collection) return;

    const listeners: CollectionLoadedListener<V2CollectionItemData>[] = [];

    const dataLoaded = () => {
      setCollectionItems(LocalData.collectionItems.documents.filter((item) => item.collection === collection));
      setLoading(false);
    };

    const companies = ArrayUtilities.arrayToMultiDimensional(
      LocalData.currentUser.companies.map((c) => c.id),
      10
    );
    listeners.push(
      ...companies.map((c) =>
        LocalData.collectionItems.setupRealtimeListenerWithFilter(
          (ref) => query(ref, where("readAccess.companies", "array-contains-any", c), where("collectionID", "==", collection.id)),
          () => {
            dataLoaded();
          }
        )
      )
    );

    const propertyRolesLists = getPropertyRolesLists();
    listeners.push(
      ...propertyRolesLists.map((r) =>
        LocalData.collectionItems.setupRealtimeListenerWithFilter(
          (ref) => query(ref, where("readAccess.propertyRoles", "array-contains-any", r), where("collectionID", "==", collection.id)),
          () => {
            dataLoaded();
          }
        )
      )
    );
    listeners.push(
      LocalData.collectionItems.setupRealtimeListenerWithFilter(
        (ref) => query(ref, where("readAccess.users", "array-contains", LocalData.currentUser.id), where("collectionID", "==", collection.id)),
        () => {
          dataLoaded();
        }
      )
    );

    return () => {
      for (const listener of listeners) listener.removeListener();
    };
  }, [collection]);

  return { collectionItems, loading };
};

/**
 * Load all global collection items once
 */
export const loadAllCollectionItems = (collection: CollectionData | undefined): Promise<V2CollectionItemData[]> => {
  if (!collection) return Promise.resolve([]);

  const promises: Promise<void>[] = [];

  const companies = ArrayUtilities.arrayToMultiDimensional(
    LocalData.currentUser.companies.map((c) => c.id),
    10
  );
  promises.push(
    ...companies.map(
      (c) =>
        new Promise<void>((resolve) => {
          LocalData.collectionItems.setupRealtimeListenerWithFilter(
            (ref) => query(ref, where("readAccess.companies", "array-contains-any", c), where("collectionID", "==", collection.id)),
            () => {
              resolve();
            }
          );
        })
    )
  );

  const flatRolesList: string[] = [];
  for (const property of LocalData.currentUser.properties) {
    const member = property.members.getData(LocalData.currentUser.id);
    for (const role of member?.roles ?? []) {
      flatRolesList.push(property.id + "/" + role.id);
    }
  }

  const roles = ArrayUtilities.arrayToMultiDimensional(flatRolesList, 10);
  promises.push(
    ...roles.map(
      (r) =>
        new Promise<void>((resolve) => {
          LocalData.collectionItems.setupRealtimeListenerWithFilter(
            (ref) => query(ref, where("readAccess.propertyRoles", "array-contains-any", r), where("collectionID", "==", collection.id)),
            () => {
              resolve();
            }
          );
        })
    )
  );

  promises.push(
    new Promise<void>((resolve) => {
      LocalData.collectionItems.setupRealtimeListenerWithFilter(
        (ref) => query(ref, where("readAccess.users", "array-contains", LocalData.currentUser.id), where("collectionID", "==", collection.id)),
        () => {
          resolve();
        }
      );
    })
  );

  return Promise.all(promises).then(() => LocalData.collectionItems.documents.filter((item) => item.collection === collection));
};

export const getAllGlobalCollectionItems = (collection: CollectionData | undefined): V2CollectionItemData[] => {
  if (!collection) return [];
  return LocalData.collectionItems.documents.filter((item) => item.collection === collection);
};

